// src/context/ToastContext.jsx
import React, { createContext, useContext, useState, useCallback, useRef } from "react";

export const ToastContext = createContext(null);

const ICONS = {
  success: "✓",
  error:   "✗",
  warning: "⚠",
  info:    "ℹ",
};

const COLOURS = {
  success: { bar: "#00FF99", bg: "#0a1a12", text: "#00FF99" },
  error:   { bar: "#ff4444", bg: "#1a0a0a", text: "#ff6666" },
  warning: { bar: "#ffaa00", bg: "#1a1500", text: "#ffcc44" },
  info:    { bar: "#4488ff", bg: "#0a0f1a", text: "#66aaff" },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timerRefs.current[id]);
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback(({
    message,
    type     = "info",
    duration = 4000,
    title    = null,
    link     = null,
  }) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev.slice(-4), { id, message, type, title, link }]);
    timerRefs.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  // Convenience methods
  const success = useCallback((msg, opts) => toast({ message: msg, type: "success", ...opts }), [toast]);
  const error   = useCallback((msg, opts) => toast({ message: msg, type: "error",   ...opts }), [toast]);
  const warning = useCallback((msg, opts) => toast({ message: msg, type: "warning", ...opts }), [toast]);
  const info    = useCallback((msg, opts) => toast({ message: msg, type: "info",    ...opts }), [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, info, dismiss }}>
      {children}

      {/* Toast container — bottom right */}
      <div style={{
        position:  "fixed",
        bottom:    "24px",
        right:     "24px",
        zIndex:    99999,
        display:   "flex",
        flexDirection: "column-reverse",
        gap:       "10px",
        maxWidth:  "380px",
        width:     "calc(100vw - 48px)",
        pointerEvents: "none",
      }}>
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast: t, onDismiss }) {
  const c = COLOURS[t.type] || COLOURS.info;

  return (
    <div
      onClick={() => { if (t.link) window.location.href = t.link; }}
      style={{
        background:   c.bg,
        border:       `1px solid ${c.bar}33`,
        borderLeft:   `4px solid ${c.bar}`,
        borderRadius: "12px",
        padding:      "12px 14px",
        display:      "flex",
        alignItems:   "flex-start",
        gap:          "10px",
        boxShadow:    "0 8px 24px rgba(0,0,0,0.4)",
        cursor:       t.link ? "pointer" : "default",
        pointerEvents: "all",
        animation:    "kwery-toast-in 0.25s ease",
      }}
    >
      {/* Icon */}
      <span style={{
        color:      c.bar,
        fontSize:   "16px",
        fontWeight: "700",
        flexShrink: 0,
        marginTop:  "1px",
      }}>
        {ICONS[t.type]}
      </span>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {t.title && (
          <p style={{ margin: "0 0 2px", color: "#fff", fontSize: "13px", fontWeight: "600" }}>
            {t.title}
          </p>
        )}
        <p style={{ margin: 0, color: "#ffffffcc", fontSize: "13px", lineHeight: "1.4" }}>
          {t.message}
        </p>
      </div>

      {/* Dismiss */}
      <button
        onClick={(e) => { e.stopPropagation(); onDismiss(t.id); }}
        style={{
          background: "none", border: "none", cursor: "pointer",
          color: "#ffffff40", fontSize: "16px", padding: "0",
          flexShrink: 0, lineHeight: 1,
        }}
      >×</button>
    </div>
  );
}

// CSS animation
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes kwery-toast-in {
      from { opacity: 0; transform: translateY(12px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0)    scale(1); }
    }
  `;
  document.head.appendChild(style);
}

export function useToast() {
  return useContext(ToastContext);
}